import React, { useState, useMemo } from 'react';
import {
  Avatar,
  Typography,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ThunderIcon from "../SvgIcons/ThunderIcon"
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import { usePriceFormatter } from '../../../hooks';
import { formatPercentage } from '../../../utils/formatting';
import { useAppSelector } from '../../../Redux/hooks';

interface ActiveLoansProps {
  portfolioListData: any;
}

const PortfolioTabel: React.FC<ActiveLoansProps> = ({ portfolioListData }) => {
  const navigation = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const formatter = usePriceFormatter();

  // Get currency info from Redux
  const currencyInfo = useAppSelector((state) => state.currency);

  const navigateToDetail = async (data) => {
    if (data?.type.toUpperCase() === "CRYPTO") {
      navigation("/crypto-detail", {
        state: { externalID: data.cmcId },
      });
    } else {
      navigation("/stockdetail", {
        state: {
          symbol: data.symbol,
          icon: data.quote?.branding?.icon_url ?? data.quote?.branding?.logo_url,
          title: "Portfolio",
        },
      });
    }
  };

  const navigateToBoosterLoan = async () => {
    navigation("/boosterloan", {
      state: {
        category: 'Activeloans',
      },
    });
  };

  const navigateToInfo = async (data) => {
    navigation("/portfolioStockInfo", {
      state: {
        symbol: data.symbol,
        stockInfo: data,
        icon: data.quote.branding.icon_url ?? data.quote.branding.logo_url,
        title: "Portfolio"
      },
    });
  };

  // Define columns for react-data-table-component
  const columns = useMemo(() => [
    {
      name: 'Company',
      selector: (row: any) => row.quote?.companyName || '',
      cell: (row: any) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={row.quote?.branding?.icon_url ?? row.quote?.branding?.logo_url}
            alt={row.symbol}
            style={{ marginRight: '8px', height: '36px', width: '36px', fontWeight: 'bold' }}
          />
          <Tooltip title={row.quote?.companyName} placement="top" arrow>
            <Typography style={{
              fontSize: '14px',
              color: 'white',
              fontWeight: 'bold',
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {row.quote?.companyName}
            </Typography>
          </Tooltip>
        </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '180px',
    },
    {
      name: 'Code',
      selector: (row: any) => row.symbol || '',
      cell: (row: any) => (
        <Typography style={{ fontSize: "16px", color: 'white', fontWeight: 'bold' }}>
          {row.symbol}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '80px',
    },
    {
      name: 'Type',
      selector: (row: any) => row.type || '',
      cell: (row: any) => (
        <Box
          sx={{
            display: "inline-block",
            padding: "2px 12px",
            borderRadius: "16px",
            background: row.type === "Crypto"
              ? "#F2674A"
              : row.type === "ETF"
                ? "#4EDF6D"
                : "#DFDD4E",
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.95rem",
            letterSpacing: "1px",
            boxShadow: "none",
          }}
        >
          {row.type === "Crypto" ? "CRYPTO" : row.type === "ETF" ? "ETF" : (row.type || "STOCK").toUpperCase()}
        </Box>
      ),
      sortable: true,
      grow: 1,
      minWidth: '100px',
    },
    {
      name: 'Avg Price.',
      selector: (row: any) => row.quote?.lastPrice || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: "16px", color: 'white', fontWeight: 'bold' }}>
          {formatter.display(row.quote?.lastPrice)}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Market Value',
      selector: (row: any) => row.value || 0,
      cell: (row: any) => (
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Typography style={{ fontSize: "16px", color: "white", fontWeight: "bold" }}>
            {formatter.balance(row.value).display}
          </Typography>
          {row?.loanAmount ? (
            <Tooltip
              placement="top"
              arrow
              title={
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    <ThunderIcon /> Booster Loan Pending Amount
                  </Box>
                  <Box
                    sx={{
                      marginTop: '8px',
                      fontWeight: 800,
                      fontSize: '20px',
                      lineHeight: '27px',
                      color: '#FFFFFF',
                      textAlign: 'center',
                    }}
                  >
                    {formatter.balance(row.loanAmount).display}
                  </Box>
                  <Box
                    sx={{
                      margin: '8px 0 6px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                      width: '100%',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingBottom: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '14px',
                        textDecoration: 'underline',
                        color: '#4A7AF2',
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                      onClick={() => { navigateToBoosterLoan() }}
                    >
                      View details
                    </span>
                  </Box>
                </Box>
              }
            >
              <span style={{ cursor: "pointer", marginLeft: 6 }}>
                <ThunderIcon />
              </span>
            </Tooltip>
          ) : null}
        </Box>
      ),
      sortable: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Profit/Loss',
      selector: (row: any) => row.gainPercent || 0,
      cell: (row: any) => (
        <Typography style={{
          fontSize: "16px",
          color: row.gainPercent == 0 ? "white" : row.gainPercent > 0 ? "#4EDF6D" : "red",
          fontWeight: 'bold'
        }}>
          {formatPercentage(row.gainPercent, { showSign: true })}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Qty.',
      selector: (row: any) => row.quantity || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: "16px", color: 'white', fontWeight: 'bold' }}>
          {formatter.quantity(row.quantity, row.type.toLowerCase()).display}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '100px',
    },
    {
      name: 'Action',
      cell: (row: any) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px', width: '100%' }}
        >
          <Chip
            label="Trade"
            style={{
              fontSize: '14px',
              backgroundColor: "#F2674A",
              color: "#FFFFFF",
              padding: '5px 23px',
              cursor: "pointer"
            }}
            onClick={(e) => {
              navigateToDetail(row);
            }}
          />
          <Chip
            label="Info"
            style={{
              fontSize: '14px',
              backgroundColor: "#4A7AF2",
              color: "#FFFFFF",
              padding: '5px 23px',
              cursor: "pointer"
            }}
            onClick={(e) => {
              navigateToInfo(row);
            }}
          />
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      right: true,
      grow: 1,
      minWidth: '200px',
    },
  ], [formatter, currencyInfo]);

  // Row navigation function for CommonDataTable
  const handleRowNavigation = (row: any): { url: string; state?: any } => {
    if (row?.type.toUpperCase() === "CRYPTO") {
      return { url: "/crypto-detail", state: { externalID: row.cmcId } };
    } else {
      return {
        url: "/stockdetail",
        state: {
          symbol: row.symbol,
          icon: row.quote?.branding?.icon_url ?? row.quote?.branding?.logo_url,
          title: "Portfolio",
        }
      };
    }
  };

  return (
    <div style={{
      marginTop: '30px',
      color: 'white',
      background: 'transparent',
      borderRadius: '20px',
      border: '2px solid #FFFFFF1A',
      padding: '20px',
      width: '100%',
      overflow: 'hidden'
    }}>
      <CommonDataTable
        data={portfolioListData || []}
        totalRows={portfolioListData?.length || 0}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        columns={columns}
        title="Portfolio"
        pagination={false}
        customStyles={{
          table: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          tableWrapper: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          headCells: {
            style: {
              fontSize: "12px",
              color: "white",
              fontWeight: "600",
              backgroundColor: "transparent",
              borderBottom: "1px solid #FFFFFF1A",
              paddingBottom: "12px",
              paddingTop: "16px",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
          cells: {
            style: {
              color: "white",
              fontSize: '16px',
              border: "none",
              padding: "12px 4px",
              backgroundColor: "transparent",
            },
          },
          rows: {
            style: {
              borderColor: "transparent !important",
              backgroundColor: 'transparent',
              width: '98% !important',
              borderRadius: "8px",
              margin: '2px 8px',
              padding: "4px",
              transition: "all 0.3s ease",
              cursor: 'pointer',
              outline: "none !important",
              '&:hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                outline: "none !important",
              },
              '&:nth-child(even)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(odd)': {
                backgroundColor: 'transparent',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(even):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
              '&:nth-child(odd):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
            },
          },
        }}
      />
    </div>
  );
};

export default PortfolioTabel;